import React, { useState } from 'react';
import { requestDeleteV2Subport, deleteSubPortfolioTwo } from '../composables/fetchAPI';
import toast from 'react-hot-toast';
import { getProgressBarColor } from '../composables/colorUtils';
import { useExchangeRates } from '../context/ExchangeRateContext';
import { usePortfolio } from '../context/PortfolioContext';
import { formatCryptoUsd } from '../utils/cryptoDecimal';

interface PortfolioItemProps {
  portfolio: any;
  onRefresh: () => void;
  onEdit?: () => void;
  onSelect?: () => void;
  parentPortId?: string;
  isPortAdmin?: boolean;
  currency?: string;
  currentPrices?: { [symbol: string]: number };
  isCrypto?: boolean; // พอร์ตคริปโต: นับเป็นเหรียญ และโชว์ยอดเล็กมากโดยไม่ปัดเป็น 0.00
}

const PortfolioItem: React.FC<PortfolioItemProps> = ({ portfolio, onRefresh, onEdit, onSelect, parentPortId, isPortAdmin, currency, currentPrices: propsCurrentPrices, isCrypto = false }) => {
  const money = (v: number) => (isCrypto ? formatCryptoUsd(v) : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const [isLoading, setIsLoading] = useState(false);
  const { exchangeRates } = useExchangeRates();
  const { displayMode } = usePortfolio(); // ✅ ดึง displayMode จาก context
  // คำนวณกำไร/ขาดทุนรวมของพอร์ต
  const totalProfitUSD = (portfolio.stocks ?? []).reduce((sum: number, stock: any) => {
    const currentPrice = propsCurrentPrices?.[stock.stockSymbol];
    if (!currentPrice) return sum;
    const profit = (currentPrice - stock.avgBuyPrice) * stock.quantity;
    return sum + profit;
  }, 0);

  const totalValueUSD = (portfolio.stocks ?? []).reduce((sum: number, stock: any) => {
    const currentPrice = propsCurrentPrices?.[stock.stockSymbol] ?? 0;
    return sum + stock.quantity * currentPrice;
  }, 0);

  // คำนวณต้นทุนรวมของพอร์ต
  const totalCostUSD = (portfolio.stocks ?? []).reduce(
    (sum: number, stock: any) => sum + (stock.quantity ?? 0) * (stock.avgBuyPrice ?? 0),
    0
  );

  // แปลงค่าเงินเป็น THB หาก currency เป็น "THB"
  const exchangeRate = currency === 'THB' ? exchangeRates['THB'] || 1 : 1;
  const totalProfit = totalProfitUSD * exchangeRate;
  const totalValue = totalValueUSD * exchangeRate;
  const totalCost = totalCostUSD * exchangeRate;

  // คำนวณเปอร์เซ็นต์กำไร/ขาดทุนรวม
  const overallProfitPercent = totalCost > 0 ? (totalProfit / totalCost) * 100 : 0;
  const currentRatio = portfolio.stockMetadata?.currentRatio ?? 0;
  const targetRatio = portfolio.expectedRatio ?? 0;
  const progressPercentage = targetRatio > 0 ? (currentRatio / targetRatio) * 100 : 0;
  const progressBarWidth = Math.min(progressPercentage, 100);
  const realizedGain = (portfolio.summary?.realizedGain || 0) * exchangeRate;
  const realizedGainPercent = totalCost > 0 ? (realizedGain / totalCost) * 100 : 0;

  return (
    <div
      className="rounded-lg px-4 py-4 flex flex-col md:flex-row items-stretch md:items-center justify-between mb-3 cursor-pointer w-full transform transition-all hover:scale-[1.01]"
      style={{ backgroundColor: 'var(--bg-item)', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.4)' }}
      onClick={onSelect}
    >
      {/* ส่วนที่ 1: ชื่อพอร์ต (กว้าง 25%) */}
      <div className="flex flex-col md:w-1/4 mb-3 md:mb-0 w-full">
        <div className="font-bold text-base md:text-lg truncate" style={{ color: 'var(--text-color)' }}>
          {portfolio.name}
        </div>
        <div className="text-xs md:text-sm" style={{ color: 'var(--yellow-text-color)' }}>
          ({portfolio.stocks?.filter((stock: any) => stock.status !== 'CLOSED').length ?? 0} {isCrypto ? 'เหรียญ' : 'หุ้น'})        </div>
      </div>

      {/* ส่วนที่ 2: มูลค่าพอร์ต & กำไรปัจจุบัน (Unrealized) (กว้าง 20%) */}
      <div className="flex flex-col items-start md:w-1/4 mb-3 md:mb-0 border-t border-gray-700/30 pt-3 md:pt-0 md:border-none w-full">
        <span className="hidden md:block text-[11px] text-gray-500 uppercase font-semibold mb-0.5">มูลค่าทั้งหมด</span>

        <div className="flex justify-between md:flex-col items-center md:items-start w-full md:w-auto">
         
          <div className="flex flex-col items-end md:items-start text-right md:text-left">
            <span className="text-base md:text-lg font-bold leading-tight" style={{ color: 'var(--text-color)' }}>
              {currency === 'THB' ? '฿' : '$'}
              {money(totalValue)}
            </span>
           
          </div>
        </div>
      </div>

 {/* ✅ ส่วนที่ 3: กำไรที่ขายไปแล้ว (Realized Gain) (กว้าง 20%) แยกออกมาเป็นคอลัมน์ใหม่ */}
      <div className="flex flex-col items-start md:w-1/5 mb-4 md:mb-0 border-t border-gray-700/30 pt-3 md:pt-0 md:border-none w-full">
        <span className="hidden md:block text-[11px] text-gray-500 uppercase font-semibold mb-0.5">กำไร/ขาดทุน"ทิพย์"</span>

        <div className="flex justify-between md:flex-col items-center md:items-start w-full md:w-auto">
          <span className="md:hidden text-xs text-gray-400 uppercase font-semibold">กำไร/ขาดทุน"ทิพย์"</span>
          <span className={`text-xs md:text-sm font-bold ${totalProfit < 0 ? 'text-red-400' : 'text-green-400'}`}>
                {totalProfit >= 0 ? '+' : ''}
                {currency === 'THB' ? '฿' : '$'}
                {money(totalProfit)}
              </span>
          {/* ✅ เพิ่มส่วนแสดงผล % ตรงนี้ */}
        <span className={`text-[10px] md:text-xs font-bold ${overallProfitPercent < 0 ? 'text-red-500/80' : 'text-green-500/80'}`}>
                ({overallProfitPercent >= 0 ? '+' : ''}{overallProfitPercent.toFixed(2)}%)
              </span>
        </div>
      </div>


      {/* ✅ ส่วนที่ 3: กำไรที่ขายไปแล้ว (Realized Gain) (กว้าง 20%) แยกออกมาเป็นคอลัมน์ใหม่ */}
      <div className="flex flex-col items-start md:w-1/5 mb-4 md:mb-0 border-t border-gray-700/30 pt-3 md:pt-0 md:border-none w-full">
        <span className="hidden md:block text-[11px] text-gray-500 uppercase font-semibold mb-0.5">กำไร "ล็อคแล้ว"</span>

        <div className="flex justify-between md:flex-col items-center md:items-start w-full md:w-auto">
          <span className="md:hidden text-xs text-gray-400 uppercase font-semibold">กำไร "ล็อคแล้ว"</span>
          <span className={`text-sm md:text-base font-bold leading-tight md:mt-0.5 ${realizedGain < 0 ? 'text-red-400' : realizedGain > 0 ? 'text-green-400' : 'text-gray-400'}`}>
            {realizedGain > 0 ? '+' : ''}
            {currency === 'THB' ? '฿' : '$'}
            {money(realizedGain)}
          </span>
          {/* ✅ เพิ่มส่วนแสดงผล % ตรงนี้ */}
          <span className={`text-[10px] font-bold ${realizedGainPercent < 0 ? 'text-red-500/80' : realizedGainPercent > 0 ? 'text-green-500/80' : 'text-gray-500'}`}>
            ( {realizedGainPercent >= 0 ? '+' : ''}{realizedGainPercent.toFixed(2)}%)
          </span>
        </div>
      </div>

      {/* ส่วนที่ 4: PROGRESS BAR (กว้าง 20%) */}
      <div className="flex flex-col items-end md:w-1/5 mb-4 md:mb-0 border-t border-gray-700/30 pt-4 md:pt-0 md:border-none w-full">
        <div className="flex flex-col items-end">
          <span
            className="text-xl md:text-base font-bold leading-tight transition-colors duration-500"
            style={{
              color: getProgressBarColor(progressPercentage, targetRatio),
              textShadow: `0 0 10px ${getProgressBarColor(progressPercentage, targetRatio)}88`
            }}
          >
            {currentRatio.toFixed(1)}%
          </span>
          <span className="text-[10px] md:text-xs font-medium -mt-1" style={{ color: 'var(--text-color)' }}>
            เป้าหมาย: {targetRatio}%
          </span>
        </div>

        <div
          className="h-2 w-32 rounded-full overflow-hidden mt-2 relative border border-gray-700/30"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)' }}
        >
          <div
            className="h-full transition-all duration-700 ease-out absolute right-0 rounded-full"
            style={{
              width: `${progressBarWidth}%`,
              background: `linear-gradient(to left, ${getProgressBarColor(progressPercentage, targetRatio)}, ${getProgressBarColor(progressPercentage, targetRatio)}CC)`,
              boxShadow: `0 0 12px ${getProgressBarColor(progressPercentage, targetRatio)}88`,
            }}
          >
            <div className="absolute top-0 left-0 w-full h-[30%] bg-white/20" />
          </div>
        </div>
      </div>

      {/* ส่วนที่ 5: Actions (กว้าง 15%) */}
      <div className="flex items-center justify-end md:w-[15%] gap-4 border-t border-gray-700/30 pt-3 md:pt-0 md:border-none w-full">
        {!isPortAdmin && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); onEdit?.(); }}
              className="text-blue-400 hover:text-blue-300 text-sm md:text-base font-bold transition-colors"
            >
              แก้ไข
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                toast((t) => (
                  <div className="p-1">
                    <p className="text-sm mb-3">ต้องการลบพอร์ต <b>{portfolio.name}</b>?</p>
                    <div className="flex justify-end gap-2">
                      <button
                        className="bg-red-500 text-white px-3 py-1 rounded text-xs font-bold"
                        onClick={async () => {
                          toast.dismiss(t.id);
                          setIsLoading(true);
                          try {
                            if (displayMode === 'V1') {
                              await deleteSubPortfolioTwo(parentPortId!, portfolio.subPortId);
                            } else {
                              await requestDeleteV2Subport(parentPortId!, portfolio.subPortId);
                            }
                            onRefresh();
                            toast.success('ลบพอร์ตย่อยสำเร็จ');
                          }
                          catch (error: any) {
                            toast.error(error?.th_message || error?.message || 'เกิดข้อผิดพลาดในการลบพอร์ตย่อย');
                          }
                          finally { setIsLoading(false); }
                        }}>ลบ</button>
                      <button className="bg-gray-600 text-white px-3 py-1 rounded text-xs" onClick={() => toast.dismiss(t.id)}>ยกเลิก</button>
                    </div>
                  </div>
                ), { duration: 5000 });
              }}
              className="text-red-400 hover:text-red-300 text-sm md:text-base font-bold transition-colors"
              disabled={isLoading}
            >
              ลบ
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PortfolioItem;